import React from 'react';
import { MilestoneMapInterval } from '@mm-shared/types/MilestoneMap1';

/**
 * Formats a date string into a human-readable relative format
 * @param dateString - ISO date string
 * @returns Formatted date like "9:55 PM Today", "10:00 AM Yesterday", "9:14 AM Jun 8, 2025"
 */
export function formatDateForComments(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();

  // Reset time to start of day for comparison
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const commentDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  // Format time as "H:MM AM/PM"
  const timeString = date.toLocaleString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  // Check if it's today
  if (commentDate.getTime() === today.getTime()) {
    return `${timeString} Today`;
  }

  // Check if it's yesterday
  if (commentDate.getTime() === yesterday.getTime()) {
    return `${timeString} Yesterday`;
  }

  // For other dates, show as "H:MM AM/PM Mon D, YYYY"
  const dateString_formatted = date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return `${timeString} ${dateString_formatted}`;
}

/**
 * Formats a date for display in a milestone map interval header
 * @param date The date to format
 * @param intervalType The type of interval (hours, days, weeks, months, quarters, years)
 * @returns Formatted date string as JSX
 */
export function formatIntervalDate(date: Date, intervalType: MilestoneMapInterval): JSX.Element {
  switch (intervalType) {
    case 'hours': {
      const timeStr = date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
      const [hours, minutes] = timeStr.split(':');
      return (
        <>
          {hours}
          <br />:{minutes}
        </>
      );
    }
    case 'days':
      return (
        <>
          {date.toLocaleDateString('en-US', { weekday: 'short' })}
          <br />
          {date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })}
        </>
      );
    case 'weeks':
      return (
        <>
          {date.toLocaleDateString('en-US', { month: 'short' })}
          <br />
          {date.toLocaleDateString('en-US', { day: 'numeric' })}
        </>
      );
    case 'months': {
      // Get the year for this month
      const year = date.getFullYear();
      // Get the last two digits of the year
      const shortYear = year.toString().slice(-2);
      return (
        <>
          '{shortYear}
          <br />
          {date.toLocaleDateString('en-US', { month: 'short' })}
        </>
      );
    }
    case 'quarters': {
      const year = date.getFullYear();
      const quarter = Math.floor(date.getMonth() / 3) + 1;
      const shortYear = year.toString().slice(-2);
      return (
        <>
          C{shortYear}
          <br />Q{quarter}
        </>
      );
    }
    case 'years':
      return <>{`'${date.getFullYear().toString().slice(-2)}`}</>;
    default:
      return (
        <>
          {date.toLocaleDateString('en-US', { month: 'short' })}
          <br />
          {date.toLocaleDateString('en-US', { day: 'numeric' })}
        </>
      );
  }
}

/**
 * Formats a date for screen reader announcements in a milestone map interval
 * @param date The date to format
 * @param intervalType The type of interval (hours, days, weeks, months, quarters, years)
 * @returns Formatted date string for screen readers
 */
export function formatIntervalDateForScreenReader(
  date: Date,
  intervalType: MilestoneMapInterval,
): string {
  switch (intervalType) {
    case 'hours': {
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
    }
    case 'days':
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    case 'weeks':
      return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    case 'months': {
      const year = date.getFullYear();
      return date.toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric',
      });
    }
    case 'quarters': {
      const year = date.getFullYear();
      const quarter = Math.floor(date.getMonth() / 3) + 1;
      return `Quarter ${quarter}, ${year}`;
    }
    case 'years':
      return date.getFullYear().toString();
    default:
      return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
  }
}
